import { Controller } from "@hotwired/stimulus"

// Fits the top-level nav to the width it actually has.
//
// The item count varies by tier (Free ~10, Pro ~14, admin 17) and the width
// varies by device, so no static rule can pick the right density: hiding the
// icons for every admin at every width (the old `navbar-dense` class) threw
// them away on a desktop with hundreds of px to spare, while a Pro user at
// laptop width got no help at all and overflowed.
//
// Instead the controller asks the browser: with the nav laid out at a given
// level, does the row overflow? It walks the levels from richest to sparsest
// and stops at the first that fits, re-running whenever the navbar's width
// changes (ResizeObserver — the browser reports the real available width,
// so this tracks window resizes, zoom, scrollbars and font loading alike):
//
//   0 full     – icon + label on every item
//   1 compact  – icons hidden (they are aria-hidden decoration; ~19px each)
//   2 dense    – compact + tighter padding and a smaller font
//   3 overflow – dense + trailing items folded into a "More" dropdown, one at
//                a time from the end, until the row fits. Items marked
//                data-navbar-fit-pin (the Admin dropdown) are never folded.
//
// Below the navbar's expand breakpoint the collapse menu is a vertical list
// and everything is restored to level 0.
export default class extends Controller {
  static targets = ["collapse", "nav", "more", "moreMenu"]
  static values = { breakpoint: { type: Number, default: 992 } }
  static classes = ["compact", "dense"]

  static LEVELS = ["full", "compact", "dense", "overflow"]

  connect() {
    this.unfoldAll() // a Turbo snapshot may have been cached mid-fold
    this.items = Array.from(this.navTarget.children).filter(li => li !== this.moreTarget)
    this.foldable = this.items.filter(li => !li.hasAttribute("data-navbar-fit-pin"))

    this.beforeCache = () => this.reset()
    document.addEventListener("turbo:before-cache", this.beforeCache)

    if (typeof ResizeObserver !== "undefined") {
      this.observer = new ResizeObserver(() => this.fit())
      this.observer.observe(this.element)
    } else {
      this.onResize = () => this.fit()
      window.addEventListener("resize", this.onResize)
    }

    this.fit()
    // Web-font swap changes label widths without resizing the navbar.
    document.fonts?.ready?.then(() => this.fit())
  }

  disconnect() {
    this.observer?.disconnect()
    if (this.onResize) window.removeEventListener("resize", this.onResize)
    document.removeEventListener("turbo:before-cache", this.beforeCache)
    this.reset()
  }

  fit() {
    this.reset()
    if (!this.expanded()) return

    if (!this.overflowing()) return this.setLevel("full")

    this.setLevel("compact")
    if (!this.overflowing()) return

    this.setLevel("dense")
    if (!this.overflowing()) return

    this.setLevel("overflow")
    this.moreTarget.classList.remove("d-none")
    for (let i = this.foldable.length - 1; i >= 0 && this.overflowing(); i--) {
      this.moreMenuTarget.prepend(this.foldable[i])
    }
    this.moreTarget.classList.toggle("navbar-fit-more-active", this.moreMenuTarget.querySelector(".nav-link.active") !== null)
  }

  // The browser's own answer to "does this row fit?". Bootstrap's expanded
  // .navbar-collapse is a flex-basis:auto item whose nowrap links can't
  // shrink, so an overflowing row doesn't scroll internally (scrollWidth ==
  // clientWidth) — the collapse itself grows past its container. Compare its
  // right edge with the container's content-box edge instead.
  overflowing() {
    const collapse = this.collapseTarget.getBoundingClientRect()
    const container = this.collapseTarget.parentElement
    const limit = container.getBoundingClientRect().right - parseFloat(getComputedStyle(container).paddingRight || 0)
    return collapse.right > limit + 0.5
  }

  expanded() {
    return window.innerWidth >= this.breakpointValue
  }

  setLevel(level) {
    this.element.dataset.navbarFitLevel = level
    const index = this.constructor.LEVELS.indexOf(level)
    this.element.classList.toggle(this.compactClass, index >= 1)
    this.element.classList.toggle(this.denseClass, index >= 2)
  }

  reset() {
    this.unfoldAll()
    this.setLevel("full")
  }

  // Put every folded item back in its original slot. Items are restored in
  // original order before the More item, which itself sits before any pinned
  // items, so appending in sequence reproduces the server-rendered layout.
  unfoldAll() {
    if (!this.hasMoreTarget) return
    const folded = Array.from(this.moreMenuTarget.children)
    folded.forEach(li => this.navTarget.insertBefore(li, this.moreTarget))
    this.moreTarget.classList.add("d-none")
    this.moreTarget.classList.remove("navbar-fit-more-active")
  }
}
